/*
 * FrameThisComment renderer
 * -------------------------------------------------------------------------
 * Single source of truth for artwork pixels. The on-screen preview and the
 * exported PNG both call renderArtwork(); nothing else draws artwork.
 *
 * All layout happens in a fixed design space (DESIGN_WIDTH units wide) and is
 * then uniformly scaled to the target canvas. Because text is measured in
 * design units, line breaks are identical at every output size — a 620px
 * preview and a 3578px export wrap in exactly the same places.
 *
 * The renderer draws ARTWORK ONLY: no picture frame, no mat board. Those are
 * product mockup chrome and live in CSS around the preview canvas. Drawing a
 * frame into the print file would mean printing a photo of a frame and then
 * putting it in a real one.
 *
 * Classic script, no modules: ES modules fail under file:// due to CORS, and
 * this app must keep working by double-clicking index.html.
 */
(function (global) {
  "use strict";

  var DESIGN_WIDTH = 1000;

  /* ------------------------------------------------------------------ *
   * Products
   *
   * trim  = final printed size in pixels at 300 dpi
   * bleed = extra pixels per side that get trimmed off / wrapped around
   * safe  = margin inside the trim that nothing important may enter
   * ------------------------------------------------------------------ */
  var PRODUCTS = {
    digital: {
      label: "Digital download",
      price: "EUR 9",
      sizeNote: "2400 x 3000 px · PNG",
      trim: [2400, 3000],
      bleed: 0,
      safe: 60,
      mockup: "none",
      copy: "A clean, high-resolution digital artwork."
    },
    poster: {
      label: "Poster A3",
      price: "EUR 39",
      sizeNote: "297 × 420 mm · 300 dpi",
      trim: [3508, 4961],
      bleed: 35, // 3 mm
      safe: 118, // 10 mm
      mockup: "paper",
      copy: "Looks excellent above a desk, questionable above a crib."
    },
    posterA2: {
      label: "Poster A2",
      price: "EUR 49",
      sizeNote: "420 × 594 mm · 300 dpi",
      trim: [4961, 7016],
      bleed: 35,
      safe: 142, // 12 mm
      mockup: "paper",
      copy: "Big enough that guests have to acknowledge it."
    },
    poster5070: {
      label: "Poster 50 × 70",
      price: "EUR 55",
      sizeNote: "500 × 700 mm · 300 dpi",
      trim: [5906, 8268],
      bleed: 35,
      safe: 142,
      mockup: "paper",
      copy: "The Scandinavian format, for a deeply unserious purpose."
    },
    canvas: {
      label: "Canvas 30 × 40",
      price: "EUR 49",
      sizeNote: "300 × 400 mm · 20 mm frame",
      trim: [3543, 4724],
      bleed: 579, // Gelato canvas file: 34 mm wrap + 15 mm bleed per edge
      safe: 118,
      mockup: "canvas",
      copy: "Gallery texture for a comment typed during a snack break."
    },
    framedCanvas: {
      label: "Framed canvas 30 x 40",
      price: "EUR 119",
      sizeNote: "300 x 400 mm canvas · floating wood frame",
      trim: [3543, 4724],
      bleed: 579, // Same slim canvas print; the outer floating frame is not printed.
      safe: 118,
      mockup: "floating-frame",
      copy: "Textured canvas, finished in a floating wooden frame."
    },
    framedA3: {
      label: "Framed print A3",
      price: "EUR 69",
      sizeNote: "297 × 420 mm · 300 dpi",
      trim: [3508, 4961],
      bleed: 35,
      safe: 118,
      mockup: "frame",
      copy: "A serious frame for words worth keeping."
    },
    framed: {
      label: "Framed print 30 × 40",
      price: "EUR 89",
      sizeNote: "300 × 400 mm · 300 dpi",
      trim: [3543, 4724],
      bleed: 35,
      safe: 118,
      mockup: "frame",
      copy: "A serious frame for a deeply unserious cultural document."
    },
    framed4050: {
      label: "Framed print 40 × 50",
      price: "EUR 129",
      sizeNote: "400 × 500 mm · 300 dpi",
      trim: [4724, 5906],
      bleed: 35,
      safe: 142,
      mockup: "frame",
      copy: "The size that ends the argument about whether it is art."
    }
  };

  /*
   * PRICES — all seven measured, 2026-07-27.
   *
   * Read from the Gelato dashboard. Landed cost = product + first-item shipping
   * to the Netherlands, both excl. VAT. Full regional data and the reasoning
   * behind each retail price are in
   * work/research/2026-07-26-gelato-real-prices.md.
   *
   *   Product          Landed NL   Retail   Margin after VAT + ~2% fees
   *   digital           EUR  0.00   EUR  15   ~EUR 12.20
   *   poster A3         EUR 19.20   EUR  39   ~EUR 12.25
   *   posterA2          EUR 21.72   EUR  49   ~EUR 17.80
   *   poster5070        EUR 23.48   EUR  55   ~EUR 20.85
   *   canvas 30x40      EUR 27.63   EUR  69   ~EUR 28.00
   *   framed 30x40      EUR 36.07   EUR  89   ~EUR 35.70
   *   framed4050        EUR 57.92   EUR 129   ~EUR 46.10
   *
   * Three things worth knowing before touching these numbers:
   *
   * 1. Retail prices INCLUDE 21% VAT, which comes off the top before margin.
   *    EUR 89 is EUR 73.55 excl. VAT.
   * 2. Poster sizes barely differ in cost — A3 to 50x70 is EUR 4.28 — while the
   *    retail gap is EUR 16. Larger posters are where the poster margin is.
   * 3. Cost is destination-dependent and not in the direction you would guess.
   *    Posters are much cheaper to the USA than to NL; framed prints are more
   *    expensive. Framed 40x50 shipping to NL is EUR 18.43 against EUR 7.62 for
   *    30x40 — that jump is why it retails at EUR 129 and not EUR 119.
   */

  function productFor(key) {
    return PRODUCTS[key] || PRODUCTS.digital;
  }

  /* ------------------------------------------------------------------ *
   * Platforms
   *
   * A platform is chrome, not a layout: handle convention, what the score
   * is called, how age reads, and which native card gets drawn. The museum,
   * poster and tabloid styles ignore all of it except the credit line.
   *
   * `accent` is the platform's default colour, applied on switch only if the
   * user has not chosen their own.
   * ------------------------------------------------------------------ */
  var PLATFORMS = {
    youtube: {
      label: "YouTube",
      accent: "#ff0033",
      handlePrefix: "@",
      scoreNoun: "likes",
      descriptor: "comment section philosopher",
      contextLabel: "Video title",
      contextPlaceholder: "The video that caused all this",
      contextFallback: "Unspecified video",
      communityLabel: null,
      agePlaceholder: "3 years ago",
      scorePlaceholder: "14K",
      handlePlaceholder: "@personwithopinions"
    },
    reddit: {
      label: "Reddit",
      accent: "#ff4500",
      handlePrefix: "u/",
      scoreNoun: "points",
      descriptor: "thread correspondent",
      contextLabel: "Post title",
      contextPlaceholder: "The thread that got out of hand",
      contextFallback: "Unspecified thread",
      communityLabel: "Subreddit",
      communityPlaceholder: "r/videos",
      agePlaceholder: "4 mo. ago",
      scorePlaceholder: "18.2k",
      handlePlaceholder: "u/personwithopinions"
    },
    tiktok: {
      label: "TikTok",
      accent: "#fe2c55",
      handlePrefix: "@",
      scoreNoun: "likes",
      descriptor: "For You Page essayist",
      contextLabel: "Video caption",
      contextPlaceholder: "the sound is not even the point",
      contextFallback: "Unspecified video",
      communityLabel: null,
      agePlaceholder: "2-14",
      scorePlaceholder: "204.5K",
      handlePlaceholder: "@personwithopinions"
    },
    instagram: {
      label: "Instagram",
      accent: "#e1306c",
      handlePrefix: "",
      scoreNoun: "likes",
      descriptor: "grid commentator",
      contextLabel: "Post caption",
      contextPlaceholder: "no caption needed honestly",
      contextFallback: "Unspecified post",
      communityLabel: null,
      agePlaceholder: "4 d",
      scorePlaceholder: "1,204",
      handlePlaceholder: "personwithopinions"
    }
  };

  function platformFor(key) {
    return PLATFORMS[key] || PLATFORMS.youtube;
  }

  /** Full export dimensions in pixels, including bleed on all four sides. */
  function exportSize(key) {
    var p = productFor(key);
    return [p.trim[0] + p.bleed * 2, p.trim[1] + p.bleed * 2];
  }

  function aspectRatio(key) {
    var s = exportSize(key);
    return s[0] / s[1];
  }

  // The wrapped canvas edges belong in the print file, not on the visible front.
  function renderTrimPreview(ctx, key, width, height, draw) {
    var p = productFor(key), full = exportSize(key);
    var buffer = document.createElement('canvas');
    buffer.width = Math.round(width * full[0] / p.trim[0]);
    buffer.height = Math.round(height * full[1] / p.trim[1]);
    var result = draw(buffer.getContext('2d'), buffer.width, buffer.height);
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(buffer, p.bleed * buffer.width / full[0], p.bleed * buffer.height / full[1],
      p.trim[0] * buffer.width / full[0], p.trim[1] * buffer.height / full[1], 0, 0, width, height);
    return result;
  }

  /* ------------------------------------------------------------------ *
   * Text layout
   * ------------------------------------------------------------------ */

  /**
   * Wrap text to maxWidth. Falls back to breaking mid-word when a single
   * token is wider than the box — YouTube comments are full of URLs and
   * unbroken character spam, and those used to run straight off the canvas.
   */
  function wrapLines(ctx, text, maxWidth, stats) {
    var lines = [];
    var paragraphs = String(text == null ? "" : text).split(/\r?\n/);
    if (stats) stats.brokeWord = false;

    paragraphs.forEach(function (para) {
      var words = para.split(/\s+/).filter(Boolean);
      if (!words.length) {
        lines.push("");
        return;
      }

      var line = "";
      words.forEach(function (word) {
        var test = line ? line + " " + word : word;
        if (ctx.measureText(test).width <= maxWidth) {
          line = test;
          return;
        }
        if (line) {
          lines.push(line);
          line = "";
        }
        if (ctx.measureText(word).width <= maxWidth) {
          line = word;
          return;
        }
        // Single token too wide: break it by character.
        if (stats) stats.brokeWord = true;
        var chunk = "";
        Array.from(word).forEach(function (ch) {
          if (chunk && ctx.measureText(chunk + ch).width > maxWidth) {
            lines.push(chunk);
            chunk = ch;
          } else {
            chunk += ch;
          }
        });
        line = chunk;
      });

      if (line) lines.push(line);
    });

    return lines.length ? lines : [""];
  }

  function ellipsize(ctx, text, maxWidth) {
    var out = String(text);
    while (out.length > 1 && ctx.measureText(out + "…").width > maxWidth) {
      out = out.slice(0, -1);
    }
    return out.replace(/[\s.,;:!?-]+$/, "") + "…";
  }

  /**
   * Shrink the font until the wrapped block fits the box in both axes.
   * Only clips as a last resort at minimum size — the old renderer truncated
   * at a hardcoded 5 lines while half the print was empty.
   */
  function fitText(ctx, text, box, opts) {
    var size, lines, lineHeight;
    var stats = {};
    var fallback = null;

    for (size = opts.max; size >= opts.min; size -= 1) {
      ctx.font = opts.font(size);
      lines = wrapLines(ctx, text, box.w, stats);
      lineHeight = size * opts.lineHeight;
      if (lines.length * lineHeight > box.h) continue;

      // Fits vertically — but if a word had to be split mid-character there is
      // usually a smaller size where it does not. Keep this as a fallback and
      // carry on shrinking; hyphen-free wrapping reads far better in print.
      if (!stats.brokeWord) {
        return { size: size, lines: lines, lineHeight: lineHeight };
      }
      if (!fallback) {
        fallback = { size: size, lines: lines, lineHeight: lineHeight };
      }
    }

    // Nothing wrapped cleanly. Prefer the largest size that at least fit the
    // box over clipping at the minimum.
    if (fallback) return fallback;

    size = opts.min;
    ctx.font = opts.font(size);
    lines = wrapLines(ctx, text, box.w, stats);
    lineHeight = size * opts.lineHeight;
    var maxLines = Math.max(1, Math.floor(box.h / lineHeight));
    if (lines.length > maxLines) {
      lines = lines.slice(0, maxLines);
      lines[maxLines - 1] = ellipsize(ctx, lines[maxLines - 1], box.w);
    }
    return { size: size, lines: lines, lineHeight: lineHeight };
  }

  /** Draw a fitted block. anchor: "top" | "middle" | "bottom" within box. */
  function drawBlock(ctx, fitted, box, align, anchor) {
    var blockHeight = fitted.lines.length * fitted.lineHeight;
    var top = box.y;
    if (anchor === "middle") top = box.y + (box.h - blockHeight) / 2;
    else if (anchor === "bottom") top = box.y + box.h - blockHeight;

    var x = box.x;
    if (align === "center") x = box.x + box.w / 2;
    else if (align === "right") x = box.x + box.w;

    ctx.textAlign = align;
    ctx.textBaseline = "alphabetic";
    fitted.lines.forEach(function (line, i) {
      ctx.fillText(line, x, top + i * fitted.lineHeight + fitted.size * 0.82);
    });
    return { top: top, height: blockHeight };
  }

  function measuredHeight(ctx, text, box, opts) {
    return fitText(ctx, text, box, opts);
  }

  /* ------------------------------------------------------------------ *
   * Helpers
   * ------------------------------------------------------------------ */

  function clean(value, fallback) {
    var text = String(value == null ? "" : value).trim();
    return text.length ? text : fallback;
  }

  function initials(name) {
    var cleaned = clean(name, "FT").replace(/^@/, "");
    var parts = cleaned.split(/[\s._-]+/).filter(Boolean).slice(0, 2);
    var out = parts.map(function (p) { return Array.from(p)[0]; }).join("");
    return (out || "FT").toUpperCase().slice(0, 2);
  }

  function sourceLabel(model) {
    var platform = PLATFORMS[model.platform] || PLATFORMS.youtube;
    var title = clean(model.videoTitle, platform.contextFallback);
    var url = clean(model.sourceUrl, "");
    if (!url) return title + " · " + platform.label;
    try {
      var host = new URL(url).hostname.replace(/^www\./, "");
      return title + " · " + host;
    } catch (err) {
      return title + " · " + platform.label;
    }
  }

  /**
   * Apply a platform's handle convention without doubling a prefix the user
   * already typed. "u/dave", "@dave" and "dave" all normalise correctly.
   */
  function formatHandle(name, prefix) {
    var raw = clean(name, "").replace(/^(u\/|@|r\/)/i, "");
    if (!raw) return prefix + "anonymous";
    return prefix + raw;
  }

  function formatCommunity(name) {
    var raw = clean(name, "").replace(/^r\//i, "");
    return raw ? "r/" + raw : "";
  }

  function roundRect(ctx, x, y, w, h, r) {
    var radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
  }

  function sans(weight, size) {
    return weight + " " + size + 'px Inter, "Helvetica Neue", Arial, sans-serif';
  }

  function serif(weight, size) {
    return weight + " " + size + 'px Georgia, "Times New Roman", serif';
  }

  function condensed(size) {
    return "900 " + size + 'px Impact, Haettenschweiler, "Arial Narrow", sans-serif';
  }

  /* ------------------------------------------------------------------ *
   * Styles
   *
   * Each receives a `safe` box in design units and must stay inside it.
   * ------------------------------------------------------------------ */

  var STYLES = {};

  /* -- shared card scaffolding ---------------------------------------- */

  /**
   * Measure the comment body, then draw a white card sized to hug it.
   * Every platform card uses this; only the header and footer rows differ.
   */
  function cardLayout(ctx, m, safe, opts) {
    var pad = safe.w * 0.06;
    var avatar = safe.w * (opts.avatar == null ? 0.085 : opts.avatar);
    var gutter = opts.inset === false ? 0 : avatar + pad * 0.7;

    var textBox = {
      x: safe.x + pad + gutter,
      y: 0,
      w: safe.w - pad * 2 - gutter,
      h: safe.h * 0.52
    };
    var body = fitText(ctx, m.comment, textBox, {
      font: function (s) { return sans(600, s); },
      min: 16, max: Math.round(safe.w * 0.075), lineHeight: 1.28
    });

    var metaSize = Math.max(12, safe.w * 0.026);
    var headH = metaSize * (opts.headRows || 1) * 1.6;
    var footH = metaSize * 2.4;
    var cardH = pad * 2 + headH + body.lines.length * body.lineHeight + footH;

    return {
      pad: pad,
      avatar: avatar,
      metaSize: metaSize,
      textBox: textBox,
      body: body,
      x: safe.x,
      y: safe.y + (safe.h - cardH) / 2,
      w: safe.w,
      h: cardH,
      headH: headH
    };
  }

  function drawCardSurface(ctx, L, radiusFactor) {
    ctx.save();
    ctx.shadowColor = "rgba(17, 24, 39, 0.16)";
    ctx.shadowBlur = L.w * 0.05;
    ctx.shadowOffsetY = L.w * 0.018;
    ctx.fillStyle = "#ffffff";
    roundRect(ctx, L.x, L.y, L.w, L.h, L.w * (radiusFactor == null ? 0.02 : radiusFactor));
    ctx.fill();
    ctx.restore();

    ctx.strokeStyle = "rgba(17, 24, 39, 0.10)";
    ctx.lineWidth = Math.max(1, L.w * 0.0016);
    roundRect(ctx, L.x, L.y, L.w, L.h, L.w * (radiusFactor == null ? 0.02 : radiusFactor));
    ctx.stroke();
  }

  function drawAvatar(ctx, L, m, accent, cx, cy) {
    ctx.fillStyle = accent;
    ctx.beginPath();
    ctx.arc(cx, cy, L.avatar / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = sans(900, L.avatar * 0.42);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(initials(m.author), cx, cy);
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";
  }

  function drawBody(ctx, L, top) {
    ctx.fillStyle = "#111827";
    ctx.font = sans(600, L.body.size);
    drawBlock(ctx, L.body, {
      x: L.textBox.x,
      y: top,
      w: L.textBox.w,
      h: L.body.lines.length * L.body.lineHeight
    }, "left", "top");
  }

  /* -- small vector glyphs (no icon font, no emoji) -------------------- */

  function triangle(ctx, cx, cy, size, up) {
    ctx.beginPath();
    if (up) {
      ctx.moveTo(cx, cy - size * 0.5);
      ctx.lineTo(cx + size * 0.5, cy + size * 0.35);
      ctx.lineTo(cx - size * 0.5, cy + size * 0.35);
    } else {
      ctx.moveTo(cx, cy + size * 0.5);
      ctx.lineTo(cx + size * 0.5, cy - size * 0.35);
      ctx.lineTo(cx - size * 0.5, cy - size * 0.35);
    }
    ctx.closePath();
    ctx.fill();
  }

  function heart(ctx, cx, cy, size) {
    var s = size / 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy + s * 0.75);
    ctx.bezierCurveTo(cx - s * 1.5, cy - s * 0.35, cx - s * 0.6, cy - s * 1.2, cx, cy - s * 0.35);
    ctx.bezierCurveTo(cx + s * 0.6, cy - s * 1.2, cx + s * 1.5, cy - s * 0.35, cx, cy + s * 0.75);
    ctx.closePath();
    ctx.fill();
  }

  /* -- platform-native cards ------------------------------------------ */

  var PLATFORM_CARDS = {};

  PLATFORM_CARDS.youtube = function (ctx, m, safe, accent) {
    var L = cardLayout(ctx, m, safe, {});
    drawCardSurface(ctx, L);
    drawAvatar(ctx, L, m, accent, L.x + L.pad + L.avatar / 2, L.y + L.pad + L.avatar / 2);

    var metaY = L.y + L.pad + L.metaSize * 1.15;
    ctx.font = sans(800, L.metaSize * 1.15);
    ctx.fillStyle = "#111827";
    ctx.fillText(m.author, L.textBox.x, metaY);
    var w = ctx.measureText(m.author).width;
    ctx.font = sans(600, L.metaSize);
    ctx.fillStyle = "#6b7280";
    ctx.fillText(m.age, L.textBox.x + w + L.metaSize * 0.9, metaY);

    drawBody(ctx, L, metaY + L.metaSize * 0.7);

    var footY = L.y + L.h - L.pad * 0.9;
    ctx.font = sans(700, L.metaSize * 0.92);
    ctx.fillStyle = "#6b7280";
    ctx.fillText(m.score + " likes     Reply", L.textBox.x, footY);
  };

  PLATFORM_CARDS.reddit = function (ctx, m, safe, accent) {
    var L = cardLayout(ctx, m, safe, { avatar: 0.062 });
    drawCardSurface(ctx, L, 0.012);

    // Thread rail — the visual tell of a Reddit comment.
    ctx.fillStyle = "rgba(17, 24, 39, 0.14)";
    ctx.fillRect(L.x + L.pad * 0.45, L.y + L.pad, Math.max(1, L.w * 0.004), L.h - L.pad * 2);

    drawAvatar(ctx, L, m, accent, L.x + L.pad + L.avatar / 2, L.y + L.pad + L.avatar / 2);

    var metaY = L.y + L.pad + L.metaSize * 1.05;
    var x = L.textBox.x;
    if (m.community) {
      ctx.font = sans(800, L.metaSize);
      ctx.fillStyle = "#111827";
      ctx.fillText(m.community, x, metaY);
      x += ctx.measureText(m.community).width + L.metaSize * 0.45;
      ctx.fillStyle = "#9ca3af";
      ctx.fillText("·", x, metaY);
      x += L.metaSize * 0.55;
    }
    ctx.font = sans(700, L.metaSize);
    ctx.fillStyle = "#4b5563";
    ctx.fillText(m.author, x, metaY);
    x += ctx.measureText(m.author).width + L.metaSize * 0.45;
    ctx.font = sans(500, L.metaSize);
    ctx.fillStyle = "#9ca3af";
    ctx.fillText("· " + m.age, x, metaY);

    drawBody(ctx, L, metaY + L.metaSize * 0.9);

    // Vote row
    var footY = L.y + L.h - L.pad * 0.95;
    var glyph = L.metaSize * 0.95;
    ctx.fillStyle = accent;
    triangle(ctx, L.textBox.x + glyph * 0.5, footY - glyph * 0.35, glyph, true);
    ctx.font = sans(800, L.metaSize * 0.95);
    ctx.fillStyle = "#111827";
    ctx.fillText(m.score, L.textBox.x + glyph * 1.3, footY);
    var sw = ctx.measureText(m.score).width;
    ctx.fillStyle = "#9ca3af";
    triangle(ctx, L.textBox.x + glyph * 1.3 + sw + glyph * 1.05, footY - glyph * 0.35, glyph, false);
    ctx.font = sans(700, L.metaSize * 0.9);
    ctx.fillStyle = "#6b7280";
    ctx.fillText("Reply    Share", L.textBox.x + glyph * 1.3 + sw + glyph * 2.1, footY);
  };

  PLATFORM_CARDS.tiktok = function (ctx, m, safe, accent) {
    var L = cardLayout(ctx, m, safe, {});
    drawCardSurface(ctx, L);
    drawAvatar(ctx, L, m, accent, L.x + L.pad + L.avatar / 2, L.y + L.pad + L.avatar / 2);

    var metaY = L.y + L.pad + L.metaSize * 1.15;
    ctx.font = sans(800, L.metaSize);
    ctx.fillStyle = "#6b7280";
    ctx.fillText(m.author, L.textBox.x, metaY);

    drawBody(ctx, L, metaY + L.metaSize * 0.8);

    var footY = L.y + L.h - L.pad * 0.9;
    ctx.font = sans(600, L.metaSize * 0.92);
    ctx.fillStyle = "#9ca3af";
    ctx.fillText(m.age + "     Reply", L.textBox.x, footY);

    // Heart + count, right-aligned like the app.
    var glyph = L.metaSize * 1.15;
    ctx.font = sans(700, L.metaSize * 0.92);
    ctx.textAlign = "right";
    ctx.fillStyle = "#6b7280";
    ctx.fillText(m.score, L.x + L.w - L.pad, footY);
    var sw = ctx.measureText(m.score).width;
    ctx.textAlign = "left";
    ctx.fillStyle = accent;
    heart(ctx, L.x + L.w - L.pad - sw - glyph * 0.75, footY - glyph * 0.3, glyph);
  };

  PLATFORM_CARDS.instagram = function (ctx, m, safe, accent) {
    // Instagram runs the username inline with the comment, so the body needs
    // an indent on its first line rather than a separate header row.
    var L = cardLayout(ctx, m, safe, {});
    drawCardSurface(ctx, L);
    drawAvatar(ctx, L, m, accent, L.x + L.pad + L.avatar / 2, L.y + L.pad + L.avatar / 2);

    var metaY = L.y + L.pad + L.metaSize * 1.15;
    ctx.font = sans(800, L.metaSize * 1.1);
    ctx.fillStyle = "#111827";
    ctx.fillText(m.author, L.textBox.x, metaY);

    drawBody(ctx, L, metaY + L.metaSize * 0.75);

    var footY = L.y + L.h - L.pad * 0.9;
    ctx.font = sans(600, L.metaSize * 0.9);
    ctx.fillStyle = "#9ca3af";
    ctx.fillText(m.age + "     " + m.score + " likes     Reply", L.textBox.x, footY);

    var glyph = L.metaSize * 1.1;
    ctx.fillStyle = accent;
    heart(ctx, L.x + L.w - L.pad - glyph * 0.5, footY - glyph * 0.3, glyph);
  };

  /* -- source capsule -------------------------------------------------- *
   *
   * An optional band below the artwork carrying the provenance: platform,
   * post title, author, date, and a one-line context note.
   *
   * Off by default, deliberately. A comment that needs a caption to land is
   * usually a weak comment, and a second joke on the wall competes with the
   * first. This exists to make context-dependent comments sellable, not to
   * decorate strong ones.
   * ------------------------------------------------------------------- */

  /** Height the capsule needs, in design units. 0 when disabled. */
  function capsuleHeight(m, safeW) {
    if (!m.showContext) return 0;
    var size = Math.max(9, safeW * 0.021);
    if (!m.showSourceDetails) return size * 3.5;
    return size * (m.contextNote ? 7.4 : 5.4);
  }

  function drawCapsule(ctx, m, box) {
    var size = Math.max(9, box.w * 0.021);

    ctx.strokeStyle = "rgba(17, 24, 39, 0.18)";
    ctx.lineWidth = Math.max(1, box.w * 0.0012);
    ctx.beginPath();
    ctx.moveTo(box.x, box.y);
    ctx.lineTo(box.x + box.w, box.y);
    ctx.stroke();

    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";

    if (!m.showSourceDetails) {
      var simpleBox = { x: box.x, y: box.y + size * 0.65, w: box.w, h: box.h - size * 0.65 };
      var simple = fitText(ctx, m.contextNote, simpleBox, {
        font: function (s) { return sans(500, s); },
        min: 8, max: size * 1.1, lineHeight: 1.3
      });
      ctx.fillStyle = "#4b5563";
      ctx.font = sans(500, simple.size);
      drawBlock(ctx, simple, simpleBox, "left", "middle");
      return;
    }

    var y = box.y + size * 2.1;
    ctx.font = sans(900, size * 0.92);
    ctx.fillStyle = "#9ca3af";
    ctx.fillText("SOURCE TRAIL", box.x, y);

    y += size * 1.9;
    ctx.font = sans(700, size * 1.05);
    ctx.fillStyle = "#4b5563";
    ctx.fillText(truncateToWidth(ctx, [m.platformLabel, m.contextTitle].filter(Boolean).join(" · "), box.w), box.x, y);

    y += size * 1.5;
    ctx.font = sans(500, size);
    ctx.fillStyle = "#9ca3af";
    ctx.fillText(truncateToWidth(ctx, [m.sourceAuthor, m.sourceAge].filter(Boolean).join(" · "), box.w), box.x, y);

    if (m.contextNote) {
      var noteBox = { x: box.x, y: y + size * 0.6, w: box.w, h: size * 2.4 };
      var note = fitText(ctx, m.contextNote, noteBox, {
        font: function (s) { return sans(500, s); },
        min: 8, max: size, lineHeight: 1.35
      });
      ctx.fillStyle = "#6b7280";
      ctx.font = sans(500, note.size);
      drawBlock(ctx, note, noteBox, "left", "top");
    }
  }

  /**
   * The platform-accurate style. Dispatches on m.platform.
   *
   * Note: this is the only style that reproduces a third-party interface, and
   * therefore the only one carrying trademark exposure. `museum`, `poster` and
   * `tabloid` quote the text alone. See DECISIONS.md.
   */
  STYLES.native = function (ctx, m, safe, accent) {
    (PLATFORM_CARDS[m.platform] || PLATFORM_CARDS.youtube)(ctx, m, safe, accent);
  };

  STYLES.museum = function (ctx, m, safe, accent) {
    var kickerSize = Math.max(11, safe.w * 0.026);
    var creditSize = Math.max(11, safe.w * 0.03);
    var hasCredit = !m.quietFooter || !!m.sourceAuthor;

    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = accent;
    ctx.font = sans(900, kickerSize);
    ctx.fillText("FRAMETHISCOMMENT.COM PRESENTS", safe.x + safe.w / 2, safe.y + kickerSize);

    var quoteBox = {
      x: safe.x + safe.w * 0.05,
      y: safe.y + kickerSize * 3.4,
      w: safe.w * 0.9,
      h: safe.h - kickerSize * 3.4 - (hasCredit ? creditSize * 5 : creditSize * 1.5)
    };
    var quote = fitText(ctx, "“" + m.comment + "”", quoteBox, {
      font: function (s) { return serif(400, s); },
      min: 18, max: Math.round(safe.w * 0.105), lineHeight: 1.22
    });
    ctx.fillStyle = "#111827";
    ctx.font = serif(400, quote.size);
    drawBlock(ctx, quote, quoteBox, "center", "middle");

    if (!hasCredit) return;

    // Rule + credit, like a wall label.
    var ruleY = safe.y + safe.h - creditSize * 3.2;
    ctx.strokeStyle = "rgba(17,24,39,0.25)";
    ctx.lineWidth = Math.max(1, safe.w * 0.0014);
    ctx.beginPath();
    ctx.moveTo(safe.x + safe.w * 0.38, ruleY);
    ctx.lineTo(safe.x + safe.w * 0.62, ruleY);
    ctx.stroke();

    ctx.fillStyle = "#4b5563";
    ctx.font = sans(700, creditSize);
    ctx.textAlign = "center";
    ctx.fillText(m.author, safe.x + safe.w / 2, ruleY + creditSize * 1.9);
    if (!m.quietFooter) {
      ctx.fillStyle = "#9ca3af";
      ctx.font = sans(500, creditSize * 0.85);
      ctx.fillText(m.descriptor, safe.x + safe.w / 2, ruleY + creditSize * 3.1);
    }
  };

  STYLES.poster = function (ctx, m, safe, accent) {
    var labelSize = Math.max(11, safe.w * 0.028);
    var separatedScreenshot = m.screenshot && m.screenshotLayout === "separate";
    var screenshotBand = separatedScreenshot ? Math.min(safe.h * 0.35, safe.w * 0.66) : 0;
    var screenshotGap = separatedScreenshot ? safe.w * 0.03 : 0;

    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = accent;
    ctx.fillRect(safe.x, safe.y, safe.w * 0.16, labelSize * 0.45);
    ctx.font = sans(900, labelSize);
    ctx.fillText("LIMITED EDITION INTERNET ARTIFACT", safe.x, safe.y + labelSize * 2.2);

    var footSize = Math.max(10, safe.w * 0.024);
    var quoteBox = {
      x: safe.x,
      y: safe.y + labelSize * 3.6,
      w: safe.w,
      h: safe.h - labelSize * 3.6 - (m.quietFooter ? 0 : footSize * 4.5) - screenshotBand - screenshotGap
    };
    var quote = fitText(ctx, m.comment.toUpperCase(), quoteBox, {
      font: function (s) { return sans(900, s); },
      min: 20, max: Math.round(safe.w * 0.145), lineHeight: 0.98
    });
    ctx.fillStyle = "#111827";
    ctx.font = sans(900, quote.size);
    drawBlock(ctx, quote, quoteBox, "left", "middle");

    if (!m.quietFooter) {
      var footBox = {
        x: safe.x,
        y: safe.y + safe.h - screenshotBand - screenshotGap - footSize * 3.2,
        w: safe.w,
        h: footSize * 3.2
      };
      var foot = fitText(ctx, m.source + ". Preserved before the reply section found it.", footBox, {
        font: function (s) { return sans(600, s); },
        min: 9, max: footSize, lineHeight: 1.35
      });
      ctx.fillStyle = "#6b7280";
      ctx.font = sans(600, foot.size);
      drawBlock(ctx, foot, footBox, "left", "bottom");
    }

    if (separatedScreenshot) {
      drawScreenshotBand(ctx, m.screenshot, {
        x: safe.x,
        y: safe.y + safe.h - screenshotBand,
        w: safe.w,
        h: screenshotBand
      }, !m.quietFooter);
    }
  };

  STYLES.tabloid = function (ctx, m, safe, accent) {
    var topSize = Math.max(11, safe.w * 0.03);
    var pad = safe.w * 0.06;
    var barH = safe.w * 0.022;

    ctx.save();
    ctx.translate(safe.x + safe.w / 2, safe.y + safe.h / 2);
    ctx.rotate(-0.012);
    ctx.translate(-safe.w / 2, -safe.h / 2);

    var box = { x: 0, y: 0, w: safe.w, h: safe.h };

    ctx.fillStyle = "#fff4c2";
    ctx.fillRect(box.x, box.y, box.w, box.h);
    ctx.fillStyle = accent;
    ctx.fillRect(box.x, box.y, box.w, barH);
    ctx.fillStyle = "#111827";
    ctx.fillRect(box.x, box.y + box.h - barH, box.w, barH);

    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#111827";
    ctx.font = sans(900, topSize);
    ctx.fillText("COMMENT SECTION REPORT", box.x + pad, box.y + barH + topSize * 2);

    var footSize = Math.max(10, safe.w * 0.026);
    var quoteBox = {
      x: box.x + pad,
      y: box.y + barH + topSize * 3.2,
      w: box.w - pad * 2,
      h: box.h - barH * 2 - topSize * 3.2 - (m.quietFooter ? 0 : footSize * 4)
    };
    var quote = fitText(ctx, m.comment.toUpperCase(), quoteBox, {
      font: function (s) { return condensed(s); },
      min: 20, max: Math.round(safe.w * 0.16), lineHeight: 0.95
    });
    ctx.fillStyle = "#111827";
    ctx.font = condensed(quote.size);
    drawBlock(ctx, quote, quoteBox, "left", "middle");

    if (!m.quietFooter) {
      var footBox = {
        x: box.x + pad,
        y: box.y + box.h - barH - footSize * 3,
        w: box.w - pad * 2,
        h: footSize * 2.4
      };
      var foot = fitText(ctx, m.author + " said it. The internet nodded, regrettably.", footBox, {
        font: function (s) { return sans(700, s); },
        min: 9, max: footSize, lineHeight: 1.3
      });
      ctx.fillStyle = "#57534e";
      ctx.font = sans(700, foot.size);
      drawBlock(ctx, foot, footBox, "left", "bottom");
    }

    ctx.restore();
  };

  /* ------------------------------------------------------------------ *
   * Main entry point
   * ------------------------------------------------------------------ */

  function normalise(state) {
    var platformKey = PLATFORMS[state.platform] ? state.platform : "youtube";
    var platform = PLATFORMS[platformKey];
    return {
      platform: platformKey,
      comment: clean(state.commentText, "This comment is currently blank, which is still more nuanced than most threads."),
      author: state.plainCredit ? clean(state.authorName, "") : formatHandle(state.authorName, platform.handlePrefix),
      sourceAuthor: state.quietFooter && !clean(state.authorName, "") ? "" : state.plainCredit ? clean(state.authorName, "") : formatHandle(state.authorName, platform.handlePrefix),
      sourceAge: clean(state.commentAge, state.quietFooter ? "" : "recently"),
      community: platform.communityLabel ? formatCommunity(state.communityName) : "",
      age: clean(state.commentAge, "recently"),
      score: clean(state.likeCount, "0"),
      scoreNoun: platform.scoreNoun,
      descriptor: platform.descriptor,
      edition: clean(state.editionName, "Certified comment artifact"),
      source: sourceLabel(state),
      style: STYLES[state.style] ? state.style : "native",
      accent: state.accentColor || platform.accent,
      screenshot: state.screenshotImage || null,
      screenshotLayout: state.screenshotLayout || "overlay",
      screenshotOnly: !!state.screenshotOnly,
      videoQr: state.videoQr || null,
      quietFooter: !!state.quietFooter,
      showSourceDetails: state.showSourceDetails !== false,
      showContext: !!state.showContext,
      contextNote: clean(state.contextNote, ""),
      contextTitle: clean(state.videoTitle, state.quietFooter ? "" : platform.contextFallback),
      platformLabel: platform.label
    };
  }

  /**
   * Draw the artwork into ctx at pxW × pxH.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {object} state       raw app state
   * @param {number} pxW         target width in pixels
   * @param {number} pxH         target height in pixels
   * @param {object} [options]   { showGuides: boolean }
   */
  function reviewCredit(value) {
    var name = String(value || "").trim();
    if (/^(?:[A-Za-z]\.?\s*){1,4}$/.test(name)) return name;
    return name.split(/\s+/)[0] || "";
  }

  function renderReview(ctx, state, pxW, pxH) {
    var scale = pxW / DESIGN_WIDTH, height = pxH / scale;
    var product = productFor(state.product), full = exportSize(state.product);
    var margin = Math.max(95, (product.bleed + product.safe) / full[0] * DESIGN_WIDTH);
    var width = DESIGN_WIDTH - margin * 2;
    var rating = Math.max(1, Math.min(5, Math.round(Number(state.reviewRating) || 5)));
    var words = String(state.commentText == null ? "" : state.commentText);
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, pxW, pxH);
    ctx.scale(scale, scale);
    ctx.fillStyle = "#fffefa";
    ctx.fillRect(0, 0, DESIGN_WIDTH, height);
    var starY = margin + height * .10, starSize = 22, gap = 63;
    for (var i = 0; i < 5; i++) {
      var x = DESIGN_WIDTH / 2 + (i - 2) * gap;
      ctx.beginPath();
      for (var point = 0; point < 10; point++) {
        var angle = -Math.PI / 2 + point * Math.PI / 5;
        var radius = point % 2 ? starSize * .45 : starSize;
        if (!point) ctx.moveTo(x + Math.cos(angle) * radius, starY + Math.sin(angle) * radius);
        else ctx.lineTo(x + Math.cos(angle) * radius, starY + Math.sin(angle) * radius);
      }
      ctx.closePath();
      ctx.fillStyle = i < rating ? "#ac812e" : "#e5e0d5";
      ctx.fill();
    }
    var screenshot = state.reviewLayout === 'combined' && state.screenshotImage;
    var screenshotHeight = screenshot ? Math.min((height - margin * 2) * .28, width * .6) : 0;
    var box = {x: margin, y: starY + 70, w: width, h: height - margin * 2 - height * .10 - 315 - (screenshot ? screenshotHeight + 40 : 0)};
    // Never clip or ellipsize a review. Even long/unbroken text must fit intact.
    var fitted = fitText(ctx, words, box, {max: 78, min: 4, lineHeight: 1.24, font: function(s) { return serif(400, s); }});
    ctx.fillStyle = "#242421";
    ctx.font = serif(400, fitted.size);
    drawBlock(ctx, fitted, box, "center", "middle");
    var creditY = box.y + box.h + 48;
    [[reviewCredit(state.reviewerName), 26, creditY], [String(state.reviewSource || ""), 20, creditY + 45], [String(state.businessName || ""), 24, height - margin - 15]].forEach(function(row) {
      var meta = fitText(ctx, row[0], {w: width, h: 40}, {max: row[1], min: 8, lineHeight: 1.1, font: function(s) { return sans(500, s); }});
      var metaSize = meta.size;
      ctx.font = sans(500, metaSize);
      while (ctx.measureText(row[0]).width > width && metaSize > 4) ctx.font = sans(500, --metaSize);
      ctx.fillStyle = "#66645d";
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.fillText(row[0], DESIGN_WIDTH / 2, row[2]);
    });
    if (screenshot) {
      var imageScale = Math.min(width / screenshot.width, screenshotHeight / screenshot.height);
      var imageW = screenshot.width * imageScale, imageH = screenshot.height * imageScale;
      ctx.drawImage(screenshot, (DESIGN_WIDTH - imageW) / 2, height - margin - 115 - screenshotHeight + (screenshotHeight - imageH) / 2, imageW, imageH);
    }
    ctx.restore();
    return {text: words, lines: fitted.lines, size: fitted.size, rating: rating, credit: reviewCredit(state.reviewerName)};
  }

  function renderArtwork(ctx, state, pxW, pxH, options) {
    if (state.entryMode === "review" && !state.screenshotOnly) return renderReview(ctx, state, pxW, pxH);
    var opts = options || {};
    var product = productFor(state.product);
    var m = normalise(state);

    var full = exportSize(state.product);
    var scale = pxW / DESIGN_WIDTH;
    var designH = pxH / scale;

    // Bleed and safe margins converted into design units.
    var bleedD = (product.bleed / full[0]) * DESIGN_WIDTH;
    var safeD = ((product.bleed + product.safe) / full[0]) * DESIGN_WIDTH;
    var qrSize = m.videoQr ? Math.min(160, (DESIGN_WIDTH - safeD * 2) * 0.18) : 0;
    var qrBand = qrSize ? qrSize + 28 : 0;

    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, pxW, pxH);
    ctx.scale(scale, scale);

    if (m.screenshotOnly && m.screenshot) {
      var imageW = DESIGN_WIDTH - safeD * 2;
      var imageH = designH - safeD * 2 - qrBand;
      var imageScale = Math.min(imageW / m.screenshot.width, imageH / m.screenshot.height);
      var printedW = m.screenshot.width * imageScale;
      var printedH = m.screenshot.height * imageScale;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, DESIGN_WIDTH, designH);
      ctx.drawImage(m.screenshot, (DESIGN_WIDTH - printedW) / 2, safeD + (imageH - printedH) / 2, printedW, printedH);
      if (m.videoQr) drawVideoQr(ctx, m.videoQr, DESIGN_WIDTH - safeD - qrSize, designH - safeD - qrBand, qrSize);
      ctx.restore();
      return;
    }

    // Background covers the full sheet including bleed.
    var bg = ctx.createLinearGradient(0, 0, DESIGN_WIDTH, designH);
    bg.addColorStop(0, "#ffffff");
    bg.addColorStop(1, "#f4f6fa");
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, DESIGN_WIDTH, designH);

    // Accent wash, kept in a corner so it never lands under the text.
    ctx.save();
    ctx.globalAlpha = 0.1;
    ctx.fillStyle = m.accent;
    ctx.beginPath();
    ctx.arc(DESIGN_WIDTH * 1.02, designH * 1.04, DESIGN_WIDTH * 0.42, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    var stripSize = Math.max(9, DESIGN_WIDTH * 0.017);
    var safe = {
      x: safeD,
      y: safeD,
      w: DESIGN_WIDTH - safeD * 2,
      h: designH - safeD * 2 - stripSize * 3.2
    };

    // The capsule takes its band off the bottom; the style gets what is left,
    // and the fit-to-box engine absorbs the difference.
    var capsule = capsuleHeight(m, safe.w);
    var artBox = { x: safe.x, y: safe.y, w: safe.w, h: safe.h - capsule - qrBand };

    if (m.screenshot && !(m.style === "poster" && m.screenshotLayout === "separate")) {
      drawScreenshot(ctx, m.screenshot, artBox);
    }

    STYLES[m.style](ctx, m, artBox, m.accent);

    if (m.videoQr) drawVideoQr(ctx, m.videoQr, safe.x + safe.w - qrSize, safe.y + safe.h - capsule - qrBand, qrSize);

    if (capsule > 0) {
      drawCapsule(ctx, m, { x: safe.x, y: safe.y + safe.h - capsule, w: safe.w, h: capsule });
    }

    // Certificate strip, inside the safe area.
    //
    // When the capsule is on it already carries the source, so the strip drops
    // to the edition alone — otherwise the piece stacks three layers of
    // attribution on top of each other.
    ctx.textBaseline = "alphabetic";
    ctx.font = sans(800, stripSize);
    ctx.fillStyle = "#9aa4b2";
    var stripY = designH - safeD + stripSize * 0.4;
    if (!m.quietFooter) {
      if (capsule === 0) {
        drawStripText(ctx, m.source, safe.x, stripY, safe.w * 0.55, "left", stripSize);
      }
      drawStripText(ctx, m.edition, safe.x + safe.w, stripY, safe.w * 0.4, "right", stripSize);
    }

    if (opts.showGuides) {
      ctx.setLineDash([DESIGN_WIDTH * 0.012, DESIGN_WIDTH * 0.012]);
      ctx.lineWidth = Math.max(1, DESIGN_WIDTH * 0.002);
      if (product.bleed > 0) {
        ctx.strokeStyle = "rgba(255, 0, 51, 0.55)";
        ctx.strokeRect(bleedD, bleedD, DESIGN_WIDTH - bleedD * 2, designH - bleedD * 2);
      }
      ctx.strokeStyle = "rgba(14, 165, 233, 0.55)";
      ctx.strokeRect(safeD, safeD, DESIGN_WIDTH - safeD * 2, designH - safeD * 2);
      ctx.setLineDash([]);
    }

    ctx.restore();
  }

  function truncateToWidth(ctx, text, maxWidth) {
    if (ctx.measureText(text).width <= maxWidth) return text;
    return ellipsize(ctx, text, maxWidth);
  }

  function drawStripText(ctx, text, x, y, maxWidth, align, startSize) {
    var cleanText = String(text || "").toUpperCase();
    var size = startSize;
    while (size > 8) {
      ctx.font = sans(800, size);
      if (ctx.measureText(cleanText).width <= maxWidth) break;
      size -= 0.5;
    }
    ctx.font = sans(800, size);
    ctx.textAlign = align;
    ctx.fillText(truncateToWidth(ctx, cleanText, maxWidth), x, y);
  }

  function drawScreenshot(ctx, img, safe) {
    var w = safe.w * 0.3;
    var ratio = img.height && img.width ? img.height / img.width : 0.66;
    var h = w * ratio;
    var maxH = safe.h * 0.3;
    if (h > maxH) {
      h = maxH;
      w = h / ratio;
    }
    var x = safe.x + safe.w - w;
    var y = safe.y + safe.h - h;
    var border = w * 0.035;

    ctx.save();
    ctx.translate(x + w / 2, y + h / 2);
    ctx.rotate(0.035);
    ctx.translate(-w / 2, -h / 2);
    ctx.shadowColor = "rgba(17,24,39,0.22)";
    ctx.shadowBlur = w * 0.12;
    ctx.shadowOffsetY = w * 0.04;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(-border, -border, w + border * 2, h + border * 2);
    ctx.shadowColor = "transparent";
    ctx.drawImage(img, 0, 0, w, h);
    ctx.restore();
  }

  function drawScreenshotBand(ctx, img, box, showLabel) {
    var pad = box.w * 0.04;
    var labelSize = Math.max(10, box.w * 0.022);
    var imageTop = box.y + (showLabel ? labelSize * 2.4 : pad);
    var maxW = box.w - pad * 2;
    var maxH = Math.max(1, box.h - (showLabel ? labelSize * 2.9 : pad * 2));
    var ratio = img.width && img.height ? img.width / img.height : 1;
    var width = Math.min(maxW, maxH * ratio);
    var height = width / ratio;
    var x = box.x + (box.w - width) / 2;
    var y = imageTop + (maxH - height) / 2;

    ctx.save();
    ctx.fillStyle = "#edf2f0";
    ctx.fillRect(box.x, box.y, box.w, box.h);
    if (showLabel) {
      ctx.fillStyle = "#45605e";
      ctx.font = sans(800, labelSize);
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
      ctx.fillText("SOURCE SCREENSHOT", box.x + pad, box.y + labelSize * 1.6);
    }
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(x - 4, y - 4, width + 8, height + 8);
    ctx.drawImage(img, x, y, width, height);
    ctx.restore();
  }

  function drawVideoQr(ctx, qr, x, y, size) {
    var modules = qr.count;
    var cell = size / (modules + 8);
    ctx.save();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(x, y, size, size);
    ctx.fillStyle = "#111111";
    for (var row = 0; row < modules; row += 1) {
      for (var col = 0; col < modules; col += 1) {
        if (qr.cells[row * modules + col]) {
          ctx.fillRect(x + (col + 4) * cell, y + (row + 4) * cell, cell + 0.04, cell + 0.04);
        }
      }
    }
    ctx.textAlign = "right";
    ctx.textBaseline = "alphabetic";
    ctx.font = sans(800, 12);
    ctx.fillStyle = "#283438";
    ctx.fillText("WATCH VIDEO", x + size, y + size + 17);
    ctx.restore();
  }

  global.FTCRenderer = {
    PRODUCTS: PRODUCTS,
    PLATFORMS: PLATFORMS,
    STYLE_KEYS: Object.keys(STYLES),
    PLATFORM_KEYS: Object.keys(PLATFORMS),
    productFor: productFor,
    platformFor: platformFor,
    exportSize: exportSize,
    aspectRatio: aspectRatio,
    renderTrimPreview: renderTrimPreview,
    renderArtwork: renderArtwork,
    reviewCredit: reviewCredit,
    renderReview: renderReview,
    // exported for the render-check harness
    _internals: {
      wrapLines: wrapLines,
      fitText: fitText,
      initials: initials,
      sourceLabel: sourceLabel,
      formatHandle: formatHandle,
      formatCommunity: formatCommunity
    }
  };
})(typeof window !== "undefined" ? window : globalThis);
